import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetCampaignsQuery,
  useDeleteCampaignMutation,
  useArchiveCampaignMutation,
  useUnarchiveCampaignMutation,
  useStartAiCampaignMutation,
  usePauseAiCampaignMutation,
} from "@/store/rest_api";
import TelemarketingComplianceDialog from "@/components/pages/power-dialer/TelemarketingComplianceDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  MoreVertical,
  Plus,
  Megaphone,
  Pencil,
  Trash2,
  Zap,
  Bot,
  BarChart2,
  Play,
  Pause,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/util/apiError";
import type Campaign from "@/model/interface/Campaign";
import { CampaignStatus, CampaignType, DialingMode } from "@/model/interface/Campaign";

const statusVariants: Record<CampaignStatus, "default" | "secondary" | "outline" | "destructive"> = {
  [CampaignStatus.DRAFT]: "secondary",
  [CampaignStatus.ACTIVE]: "default",
  [CampaignStatus.PAUSED]: "outline",
  [CampaignStatus.COMPLETED]: "secondary",
  [CampaignStatus.ARCHIVED]: "secondary",
};

const statusLabels: Record<CampaignStatus, string> = {
  [CampaignStatus.DRAFT]: "Draft",
  [CampaignStatus.ACTIVE]: "Active",
  [CampaignStatus.PAUSED]: "Paused",
  [CampaignStatus.COMPLETED]: "Completed",
  [CampaignStatus.ARCHIVED]: "Archived",
};

const dialingModeLabels: Record<DialingMode, string> = {
  [DialingMode.PREVIEW]: "Preview",
  [DialingMode.POWER]: "Power",
  [DialingMode.PREDICTIVE]: "Predictive",
  [DialingMode.PROGRESSIVE]: "Progressive",
  [DialingMode.AI_AGENT]: "AI Agent",
};

type CampaignListView = "CURRENT" | "ARCHIVED";

export default function CampaignListPage() {
  const navigate = useNavigate();
  const { data: campaigns, isLoading, error } = useGetCampaignsQuery();
  const [deleteCampaign] = useDeleteCampaignMutation();
  const [archiveCampaign] = useArchiveCampaignMutation();
  const [unarchiveCampaign] = useUnarchiveCampaignMutation();
  const [startCampaign] = useStartAiCampaignMutation();
  const [pauseCampaign] = usePauseAiCampaignMutation();
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);
  const [campaignToArchive, setCampaignToArchive] = useState<Campaign | null>(null);
  const [sequenceToStart, setSequenceToStart] = useState<Campaign | null>(null);
  const [view, setView] = useState<CampaignListView>("CURRENT");

  const allCampaigns = campaigns ?? [];
  const archivedCampaigns = allCampaigns.filter((campaign) => campaign.status === CampaignStatus.ARCHIVED);
  const currentCampaigns = allCampaigns.filter((campaign) => campaign.status !== CampaignStatus.ARCHIVED);
  const isArchivedView = view === "ARCHIVED";
  const visibleCampaigns = isArchivedView ? archivedCampaigns : currentCampaigns;

  const handleDelete = async () => {
    if (!campaignToDelete) return;
    try {
      await deleteCampaign(campaignToDelete.id).unwrap();
      toast.success("Campaign deleted");
      setCampaignToDelete(null);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to delete campaign"));
    }
  };

  const handleArchive = async () => {
    if (!campaignToArchive) {
      return;
    }
    try {
      await archiveCampaign(campaignToArchive.id).unwrap();
      toast.success("Campaign archived");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to archive campaign"));
    } finally {
      setCampaignToArchive(null);
    }
  };

  const handleUnarchive = async (campaign: Campaign) => {
    try {
      await unarchiveCampaign(campaign.id).unwrap();
      toast.success("Campaign restored");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to restore campaign"));
    }
  };

  const handleStartSequenceConfirmed = async () => {
    if (!sequenceToStart) return;
    try {
      await startCampaign({ id: sequenceToStart.id, acknowledgedRegistration: true }).unwrap();
      toast.success("Sequence started");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to start sequence"));
    } finally {
      setSequenceToStart(null);
    }
  };

  const handlePauseSequence = async (campaign: Campaign) => {
    try {
      await pauseCampaign(campaign.id).unwrap();
      toast.success("Sequence paused");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to pause sequence"));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading campaigns...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-destructive">Failed to load campaigns</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Campaigns</h1>
          <p className="text-muted-foreground">Manage your calling campaigns</p>
        </div>
        <Button onClick={() => navigate("/campaigns/new")}>
          <Plus className="mr-2 h-4 w-4" />
          Create Campaign
        </Button>
      </div>

      {allCampaigns.length > 0 && (
        <Tabs value={view} onValueChange={(value) => setView(value as CampaignListView)}>
          <TabsList>
            <TabsTrigger value="CURRENT">Campaigns</TabsTrigger>
            <TabsTrigger value="ARCHIVED">
              Archived
              {archivedCampaigns.length > 0 && (
                <span className="ml-1.5 text-muted-foreground tabular-nums">{archivedCampaigns.length}</span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {visibleCampaigns.length === 0 && !isArchivedView ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Megaphone className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No campaigns yet</h3>
            <p className="text-muted-foreground text-center mb-4 max-w-md">
              Create a campaign to start power dialing your contacts.
            </p>
            <Button onClick={() => navigate("/campaigns/new")}>
              <Plus className="mr-2 h-4 w-4" />
              Create Your First Campaign
            </Button>
          </CardContent>
        </Card>
      ) : visibleCampaigns.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Archive className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">No archived campaigns</h3>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>{isArchivedView ? "Archived Campaigns" : "All Campaigns"}</CardTitle>
            <CardDescription>
              {visibleCampaigns.length} {visibleCampaigns.length === 1 ? "campaign" : "campaigns"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleCampaigns.map((campaign: Campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium flex items-center gap-1.5">
                          {campaign.dialingMode === "AI_AGENT" && (
                            <Bot className="h-3.5 w-3.5 text-violet-500 shrink-0" />
                          )}
                          {campaign.name}
                        </div>
                        {campaign.description && (
                          <div className="text-sm text-muted-foreground truncate max-w-xs">{campaign.description}</div>
                        )}
                        {campaign.dialingMode === "AI_AGENT" && campaign.aiAgent?.name && (
                          <div className="text-xs text-violet-600/80 mt-0.5">{campaign.aiAgent.name}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariants[campaign.status]}>{statusLabels[campaign.status]}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm flex items-center gap-1">
                        {campaign.dialingMode === "AI_AGENT" && (
                          <Bot className="h-3 w-3 text-violet-500" />
                        )}
                        {campaign.dialingMode ? dialingModeLabels[campaign.dialingMode] : "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {campaign.phoneNumber?.alias || campaign.phoneNumber?.number || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {campaign.createdAt ? new Date(campaign.createdAt).toLocaleDateString() : "—"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {campaign.status === CampaignStatus.ARCHIVED ? (
                            <DropdownMenuItem onClick={() => handleUnarchive(campaign)}>
                              <ArchiveRestore className="mr-2 h-4 w-4" />
                              Unarchive
                            </DropdownMenuItem>
                          ) : campaign.type === CampaignType.SEQUENCE ? (
                            campaign.status === CampaignStatus.ACTIVE ? (
                              <DropdownMenuItem onClick={() => handlePauseSequence(campaign)}>
                                <Pause className="mr-2 h-4 w-4" />
                                Pause Sequence
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => setSequenceToStart(campaign)}>
                                <Play className="mr-2 h-4 w-4" />
                                Start Sequence
                              </DropdownMenuItem>
                            )
                          ) : campaign.dialingMode === "AI_AGENT" ? (
                            <DropdownMenuItem onClick={() => navigate(`/campaigns/${campaign.id}/live`)}>
                              <BarChart2 className="mr-2 h-4 w-4" />
                              Live Dashboard
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => navigate(`/power-dialer/${campaign.id}`)}>
                              <Zap className="mr-2 h-4 w-4" />
                              Launch Power Dialer
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {campaign.status === CampaignStatus.ARCHIVED ? (
                            <DropdownMenuItem onClick={() => setCampaignToDelete(campaign)} className="text-destructive">
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => setCampaignToArchive(campaign)}>
                              <Archive className="mr-2 h-4 w-4" />
                              Archive
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <TelemarketingComplianceDialog
        open={!!sequenceToStart}
        campaignId={sequenceToStart?.id ?? null}
        source="ai"
        onOpenChange={(open) => {
          if (!open) {
            setSequenceToStart(null);
          }
        }}
        onConfirm={handleStartSequenceConfirmed}
      />

      <AlertDialog open={!!campaignToDelete} onOpenChange={() => setCampaignToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Campaign</AlertDialogTitle>
            <AlertDialogDescription>
              Delete <span className="font-semibold">{campaignToDelete?.name}</span>? It disappears from your
              campaigns. Its calls and reports stay.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!campaignToArchive} onOpenChange={() => setCampaignToArchive(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Campaign</AlertDialogTitle>
            <AlertDialogDescription>
              Archive <span className="font-semibold">{campaignToArchive?.name}</span>? It moves to the Archived tab
              and can't be started until you unarchive it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
